'use client'

import { useEffect, useState } from 'react'
import { Minus, Plus } from 'lucide-react'

/**
 * Quantity selector with a typeable field between the −/+ buttons so a customer
 * can jump straight to e.g. 100 instead of tapping + a hundred times. Mobile
 * gets the numeric keypad (`inputMode="numeric"`). The value is clamped to
 * [min, max]; the field stays editable (can be briefly empty while typing) and
 * snaps back into range on blur.
 */
export default function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = 9999,
  size = 'md',
  disabled = false,
  ariaLabel = 'Aantal',
  tone = 'light',
  className = '',
}: {
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  /** 'compact' keeps md's touch targets but takes less horizontal room. */
  size?: 'sm' | 'compact' | 'md'
  disabled?: boolean
  ariaLabel?: string
  /** 'night' is de variant voor een donkere seizoenspagina (Sinterklaas): goud op nachtblauw. */
  tone?: 'light' | 'night'
  className?: string
}) {
  const [draft, setDraft] = useState(String(value))

  // Keep the visible field in sync when the value changes from outside
  // (e.g. the +/- buttons or a parent reset).
  useEffect(() => { setDraft(String(value)) }, [value])

  const clamp = (n: number) => Math.min(max, Math.max(min, n))

  const commit = (raw: string) => {
    const n = parseInt(raw.replace(/[^\d]/g, ''), 10)
    const next = Number.isFinite(n) ? clamp(n) : min
    onChange(next)
    setDraft(String(next))
  }

  const dims =
    size === 'sm'      ? { btn: 'w-7 h-7',   icon: 14, field: 'w-10 h-7 text-sm',   gap: 'gap-1.5' } :
    size === 'compact' ? { btn: 'w-9 h-9',   icon: 16, field: 'w-9 h-9 text-base',  gap: 'gap-0.5' } :
                         { btn: 'w-10 h-10', icon: 16, field: 'w-14 h-10 text-base', gap: 'gap-1.5' }

  const t = tone === 'night'
    ? { box: 'border-[#D8A646] bg-transparent', btn: 'text-[#D8A646] hover:bg-[#D8A646]/15', field: 'text-[#E6EDF2] focus:bg-white/5' }
    : { box: 'border-[#D2691E]/30 bg-white', btn: 'text-[#8B4513] hover:bg-[#8B4513]/10', field: 'text-[#2C1810] focus:bg-[#8B4513]/5' }

  return (
    <div className={`inline-flex items-center ${dims.gap} rounded-xl border ${t.box} p-1 ${className}`}>
      <button
        type="button"
        onClick={() => onChange(clamp(value - 1))}
        disabled={disabled || value <= min}
        className={`${dims.btn} flex items-center justify-center rounded-lg ${t.btn} disabled:opacity-30 disabled:hover:bg-transparent transition-colors`}
        aria-label="Eén minder"
      >
        <Minus size={dims.icon} />
      </button>

      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => {
          // Allow only digits; keep it editable (empty allowed) while typing.
          const cleaned = e.target.value.replace(/[^\d]/g, '')
          setDraft(cleaned)
          if (cleaned !== '') {
            const n = parseInt(cleaned, 10)
            if (Number.isFinite(n)) onChange(clamp(n))
          }
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
        className={`${dims.field} text-center font-bold ${t.field} tabular-nums bg-transparent outline-none rounded-lg`}
      />

      <button
        type="button"
        onClick={() => onChange(clamp(value + 1))}
        disabled={disabled || value >= max}
        className={`${dims.btn} flex items-center justify-center rounded-lg ${t.btn} disabled:opacity-30 disabled:hover:bg-transparent transition-colors`}
        aria-label="Eén meer"
      >
        <Plus size={dims.icon} />
      </button>
    </div>
  )
}
